import { last } from 'lodash';
import { nanoid } from 'nanoid';
import type { Poll } from 'schema/poll';
import { AnswerState, ICState } from 'schema/state';
import { getEngine } from 'shared/src/utils';
import { assign, PromiseActorLogic, setup } from 'xstate';
import { back, change, next, track, TrackActionParams, updateMediaProgress, updateOthers } from './actions';
import { createDefaultState } from './actions/creator';
import {
  markAnswerAnswered,
  markAnswerAutoCommited,
  markAnswerSkipped,
  markAnswerTracked,
  markAnswerValidity,
  markQuestionExpired,
  markUnanswerableValidity,
  reduceFirst,
} from './actions/reducers';
import { validateAnswers, validateAnswersOnChange, validateUnanswerable } from './actions/validate';
import type { ILoginProvider } from './authTypes';
import { pollPreStartTransform } from './external/pollPreStartTransformer';
import { isAuthQuestion } from './guards';
import { assertPollInvariants } from './machine/asserts';
import { OauthUser } from './machine/authState';
import type { InterpreterIntl } from './machine/interpreter';
import { TrackingError } from './machine/pixelSender';
import { IMediaAnalytics, MEDIA_EVENTS } from './mediaAnalyticsTypes';
import {
  isFinalWithZeroAutoClose,
  selectDisplayedOrAllGroupQuestions,
  selectGroup,
  selectQuestion,
  selectRelevantQuestions,
} from './selects';
import type { ITracking } from './trackingTypes';
import {
  findLastInStack,
  findLastInStackSafe,
  findLastStoredQuestionSafe,
  getMapFromStack,
  getOverallResult,
  hasFeedback,
  hasImageFeedback,
  questionHasDisclaimer,
} from './utils';

type ShouldTrackingDependentActionBeResolved = (params: { state: ICState; poll: Poll }) => boolean;

export interface PollMachineContext {
  poll: Poll;
  state: ICState;
  nextState?: ICState;
  /** Состояние восстановленное из хранилища */
  user: OauthUser | undefined;
  nextCalculatorError: Error | undefined;
  trackingError: TrackingError | undefined;
  initServerPassPollError: Error | undefined;
  retryCount: number;
  /** Указывает, начал ли взаимодействие с машиной пользователь */
  hasStarted: boolean;
  /** Список отложенных событий, которые должны быть выполнены по условию при обновлении трекинга */
  trackingDependentActionList: {
    action: Omit<ExternalAction, 'state'>;
    shouldBeResolved: ShouldTrackingDependentActionBeResolved;
  }[];
}
export type PollMachineEvents =
  | {
      type: 'change';
      questionId: string;
      answeredIds: string[];
      answersOrder?: number[];
      touchedBy?: NonNullable<AnswerState['lastTouchedBy']>;
    }
  | {
      type: 'continueAuthenticated';
    }
  | {
      type: 'skipAuthentication';
    }
  | {
      type: 'startAuthentication';
    }
  | {
      type: 'refreshAuthentication';
    }
  | {
      type: 'othersChange';
      questionId: string;
      value: string;
      touchedBy?: NonNullable<AnswerState['lastTouchedBy']>;
    }
  | {
      type: 'failedChangeValidation';
    }
  | {
      type: 'successfulChangeValidation';
    }
  | {
      type: 'answer';
    }
  | {
      type: 'tryAnswer';
    }
  | {
      type: 'back';
    }
  | {
      type: 'skip';
    }
  | {
      type: 'globalTimerStart';
    }
  | {
      type: 'fileUpload';
      questionId: string;
      fileName: string;
      path: string | null;
    }
  | {
      type: 'welcomeClick';
    }
  | {
      type: 'retryLoadingAuthState';
    }
  | ({
      type: 'trackStatusUpdate';
    } & TrackActionParams)
  | {
      type: 'mediaProgress';
      mediaProgress: 'start' | 'firstQuartile' | 'midpoint' | 'thirdQuartile' | 'complete';
      questionId: string;
    }
  | {
      type: 'linkClick';
      external_url: string;
    }
  | {
      type: 'invokeDeeplink';
      deeplink: DeeplinkAction['deeplink'];
    }
  | {
      type: 'finalClick';
      finalId: string;
    }
  | { type: 'questionFocus' }
  | ({
      type:
        | 'xstate.error.actor.pixelSender'
        | 'xstate.error.actor.nextCalculator'
        | 'xstate.error.actor.beforeAuthStateRestorer'
        | 'xstate.error.actor.beforeAuthStateDumper'
        | 'xstate.error.actor.authStateLoader'
        | 'xstate.error.actor.initServerPassPoll'
        | 'xstate.error.actor.externalLogicRulesLoader'
        | 'xstate.error.actor.persistentStateRestorer';
    } & (
      | {
          error: Error;
        }
      | Record<string, never>
    ))
  | {
      type: 'xstate.done.actor.externalLogicRulesLoader';
      output: NonNullable<ICState['externalLogicRules']>;
    }
  | {
      type: 'xstate.done.actor.pixelSender';
      output: string | null;
    }
  | {
      type: 'xstate.done.actor.authStateLoader';
      output: OauthUser | undefined;
    }
  | {
      type: 'xstate.done.actor.initServerPassPoll';
      output: {
        poll: Poll;
        questionsIndices?: string[];
        links: ICState['links'];
        externalLogicRules: ICState['externalLogicRules'];
      };
    }
  | {
      type: 'xstate.done.actor.persistentStateRestorer';
      output: ICState | undefined;
    }
  | {
      type: 'xstate.done.actor.beforeAuthStateRestorer';
      output: ICState | undefined;
    }
  | {
      type: 'xstate.done.actor.nextCalculator';
      output: { poll: Poll; state: ICState };
    }
  | {
      type: 'retryPixelSend';
    }
  | {
      type: 'retryNextCalculator';
    }
  | {
      type: 'retryInitServerPassPoll';
    }
  | {
      type: 'next';
    }
  | {
      type: 'close';
    }
  | {
      type: 'mediaProgressChange';
      questionId: string;
      progress: number;
    };

type SimpleExternalAction<T extends string> = {
  state: ICState;
  poll: Poll;
  type: T;
};

type OverallAction = SimpleExternalAction<'overall'> & { overallResult: ReturnType<typeof getOverallResult> };

type ChangeAnswerAction = SimpleExternalAction<'changeAnswer'> & {
  questionId: string;
  answeredIds: string[];
  answersOrder: number[] | undefined;
};

type CloseAction = SimpleExternalAction<'close'> & {
  unsafe: boolean;
  eventId: string;
};

type AutocloseAction = SimpleExternalAction<'autoclose'> & {
  unsafe: boolean;
  eventId: string;
};

type DeeplinkPayload =
  | {
      method: 'complete';
    }
  | {
      method: 'close';
    }
  | {
      method: 'deeplink';
      query: `?deeplink=${string}`;
    };

export type DeeplinkAction = SimpleExternalAction<'deeplink'> & {
  deeplink: {
    by: 'location' | 'heuristic';
    payload: DeeplinkPayload | DeeplinkPayload[];
    onResolve?: VoidFunction;
  };
};

export type ExternalAction =
  | SimpleExternalAction<'start'>
  | SimpleExternalAction<'load'>
  | SimpleExternalAction<'done'>
  | SimpleExternalAction<'done_requests'>
  | SimpleExternalAction<'wrong_configuration'>
  | SimpleExternalAction<'completed'>
  | SimpleExternalAction<'passed'>
  | SimpleExternalAction<'ready'>
  | SimpleExternalAction<'network_error'>
  | SimpleExternalAction<'ready_after_error'>
  | SimpleExternalAction<'clear_token_in_storage'>
  | OverallAction
  | DeeplinkAction
  | ChangeAnswerAction
  | CloseAction
  | AutocloseAction;

export type InvokeAction = (action: ExternalAction) => void;

export type PollMachine = ReturnType<typeof createPollMachine>;
export type SendError = (
  error: Error,
  options?: {
    state?: ICState;
    attachAuth?: boolean;
    answerState?: AnswerState;
    severity?: 'MINOR' | 'MAJOR' | 'CRITICAL';
    actualError?: unknown;
    additionalInfo?: Record<string, string | number | undefined | null>;
  },
) => void;

export type SendNextStep = (data: { poll: Poll; state: ICState }) => Promise<{
  patch: {
    questionDecks: Poll['questionDecks'];
    questions: Poll['questions'];
    finals: Poll['finals'];
    groups: Poll['groups'];
    sets: Poll['sets'];
  };
  state: ICState;
}>;

export type SendInitPoll = (data: { poll: Poll; state: ICState }) => Promise<{
  questionsIndices?: string[];
  links: ICState['links'];
  externalLogicRules?: ICState['externalLogicRules'];
  poll: Poll;
}>;

export function createPollMachine({
  poll: originalPoll,
  tracking,
  OAuth,
  intl,
  sendError,
  isDebugMode,
  invokeAction,
  mediaAnalitics: { resolveAllPendingEvents, hasPendingEvents },
  externalLogicRules,
  preinitPollsIc,
}: {
  poll: Poll;
  tracking: Pick<
    ITracking,
    | 'load'
    | 'setUser'
    | 'setPoll'
    | 'welcomeClick'
    | 'upload'
    | 'finalClick'
    | 'trackMediaProgress'
    | 'isStarted'
    | 'init'
    | 'linkClick'
  >;
  OAuth: Pick<ILoginProvider, 'fetchAuthorizationCode' | 'getAccessToken'>;
  strict?: boolean;
  intl: InterpreterIntl;
  isDebugMode?: boolean;
  sendError: SendError;
  invokeAction: InvokeAction;
  mediaAnalitics: IMediaAnalytics;
  externalLogicRules: ICState['externalLogicRules'] | undefined;
  preinitPollsIc?: string | null;
}) {
  assertPollInvariants(originalPoll);
  const defaultState = createDefaultState(preinitPollsIc);
  return setup({
    types: {} as {
      context: PollMachineContext;
      events: PollMachineEvents;
    },
    actors: {} as {
      authStateLoader: PromiseActorLogic<PollMachineContext['user'], PollMachineContext, any>;
      initServerPoll: PromiseActorLogic<unknown, PollMachineContext, any>;
      befareAuthStateRestorer: PromiseActorLogic<unknown, PollMachineContext, any>;
      nextCalculator: PromiseActorLogic<Pick<PollMachineContext, 'poll' | 'state'>, PollMachineContext, any>;
      pixelSender: PromiseActorLogic<string | null, PollMachineContext, any>;
      beforeAuthStateDumper: PromiseActorLogic<void, PollMachineContext, any>;
      initServerPassPoll: PromiseActorLogic<Partial<ICState>, PollMachineContext, any>;
      externalLogicRulesLoader: PromiseActorLogic<unknown, PollMachineContext, any>;
      persistentStateRestorer: PromiseActorLogic<ICState | undefined, PollMachineContext, any>;
      beforeAuthStateRestorer: PromiseActorLogic<ICState | undefined, PollMachineContext, any>;
      immediatelyUpdateStorageState: PromiseActorLogic<unknown, PollMachineContext, any>;
    },
    actions: {
      doSetExternalLogicRules: assign(({ context: { state }, event }) => {
        if (event.type === 'xstate.done.actor.externalLogicRulesLoader') {
          return {
            state: {
              ...state,
              externalLogicRules: event.output,
            },
          };
        }
        return {};
      }),
      doSetLastPixelHash: assign(({ context: { state }, event }) => {
        if (event.type === 'xstate.done.actor.pixelSender') {
          return {
            state: {
              ...state,
              lastPixelHash: event.output,
            },
          };
        }
        return {};
      }),
      doLogExternalLogicRulesError: ({ context: { state }, event }) => {
        if (event.type === 'xstate.error.actor.externalLogicRulesLoader') {
          sendError(new Error('externalLogicRulesLoader service invocation error'), {
            state,
            severity: 'MAJOR',
            actualError: event.error,
          });
        } else {
          sendError(new Error('Unknown event was sent to doLogExternalLogicRulesError: ' + event.type), { state });
        }
      },
      doLogAuthErrorAfterRedirect: ({ context: { state } }) => {
        sendError(new Error('Unauthorized after redirect'), { state, attachAuth: true, severity: 'MAJOR' });
      },
      doLogStorageError: ({ context: { state }, event }) => {
        if (
          event.type === 'xstate.error.actor.persistentStateRestorer' ||
          event.type === 'xstate.error.actor.beforeAuthStateRestorer' ||
          event.type === 'xstate.error.actor.beforeAuthStateDumper' ||
          event.type === 'xstate.error.actor.authStateLoader'
        ) {
          sendError(new Error('authStateLoader service invocation error'), {
            state,
            attachAuth: true,
            actualError: event.error,
          });
        } else {
          sendError(new Error('Unknown event was sent to doLogStorageError: ' + event.type), {
            state,
            attachAuth: true,
          });
        }
      },
      doRedirectToLogin: () => {
        const LOGIN_DELAY = 100; // Задержка для отправки quiz-start
        setTimeout(() => {
          OAuth.fetchAuthorizationCode();
        }, LOGIN_DELAY);
      },
      doChangeToAuthorizedAnswer: assign(({ context: { poll, state } }) => {
        if (!state.questionId) return {};
        const authQuestion = selectQuestion(poll, state.questionId);
        if (authQuestion.data.type !== 'POLL_AUTH') return {};
        const [yesAnswer] = authQuestion.data.answers;
        if (!yesAnswer) return {};

        return {
          state: change(
            poll,
            state,
            {
              questionId: authQuestion.id,
              answeredIds: [yesAnswer.id],
              touchedBy: 'user',
            },
            intl,
          ),
        };
      }),
      doChangeToUnauthorizedAnswer: assign(({ context: { poll, state } }) => {
        if (!state.questionId) return {};
        const authQuestion = selectQuestion(poll, state.questionId);
        if (authQuestion.data.type !== 'POLL_AUTH') return {};
        const [, noAnswer] = authQuestion.data.answers;
        if (!noAnswer) return {};

        return {
          state: change(
            poll,
            state,
            {
              questionId: authQuestion.id,
              answeredIds: [noAnswer.id],
              touchedBy: 'user',
            },
            intl,
          ),
        };
      }),
      doSetTrackingUser: ({ context: { user } }) => {
        tracking.setUser(user ?? null);
      },
      doSetTrackingUserIfHadAnsweredAuthSuccessfully: ({ context: { poll, state, user } }) => {
        const authQuestion = poll.questions.find(isAuthQuestion);
        if (authQuestion) {
          const answerState = findLastInStackSafe(state.answersStack, authQuestion.id);
          if (answerState) {
            const yesAnswerId = authQuestion.data.answers[0]?.id;
            const choosedYesAnswer = !!answerState.answeredIds?.find(id => id === yesAnswerId);
            if (answerState.isAnswered && choosedYesAnswer) {
              if (!user?.id) {
                sendError(new Error('User had answered auth question and is not authenticated!'), {
                  state,
                  attachAuth: true,
                  severity: 'MAJOR',
                });
              } else {
                tracking.setUser(user);
              }
            }
          } else if (user?.id) {
            console.warn("User haven't answered auth question but is authenticated");
          }
        } else if (user?.id) {
          console.warn("Poll doesn't have auth question but user is authenticated");
        }
      },
      doBack: assign(({ context: { state, poll } }) => ({
        state: back(poll, state),
      })),
      doMarkTimeExpired: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll }).reduce(markQuestionExpired, state),
      })),
      doChange: assign(({ context: { state, poll }, event }) => {
        if (event.type !== 'change') {
          return {};
        }

        const { questionId, answeredIds, answersOrder, touchedBy = 'user' } = event;

        if (poll.provideParentOverall) {
          invokeAction({
            type: 'changeAnswer',
            state,
            poll,
            questionId,
            answeredIds,
            answersOrder,
          });
        }

        return {
          state: change(
            poll,
            state,
            {
              questionId,
              answeredIds,
              answersOrder,
              touchedBy,
            },
            intl,
          ),
        };
      }),
      doFileUpload: assign(({ context: { state, poll }, event }) => {
        return {
          state:
            event.type !== 'fileUpload'
              ? state
              : updateOthers(
                  poll,
                  state,
                  {
                    questionId: event.questionId,
                    value: event.path ?? '',
                    touchedBy: 'user',
                  },
                  intl,
                ),
        };
      }),
      doTrackFileUpload: ({ event }) => {
        if (event.type === 'fileUpload') {
          tracking.upload({ fileName: event.fileName, path: event.path, questionId: event.questionId });
        }
      },
      doOthersChange: assign(({ context: { state, poll }, event }) => {
        return {
          state:
            event.type !== 'othersChange'
              ? state
              : updateOthers(
                  poll,
                  state,
                  {
                    questionId: event.questionId,
                    value: event.value,
                    touchedBy: event.touchedBy ?? 'user',
                  },
                  intl,
                ),
        };
      }),
      doSetNextState: assign(({ event }) => {
        if (event.type === 'xstate.done.actor.nextCalculator') {
          tracking.setPoll(event.output.poll);
          return { poll: event.output.poll, nextState: event.output.state };
        }
        return {};
      }),
      doTrackStatusUpdate: assign(({ context: { state, poll, trackingDependentActionList }, event }) => {
        if (event.type !== 'trackStatusUpdate') {
          return {};
        }

        const nextState = track(poll, state, {
          trackingState: event.trackingState,
          tracking: event.tracking,
        });

        const remainingActions: PollMachineContext['trackingDependentActionList'] = [];
        for (const action of trackingDependentActionList) {
          if (action.shouldBeResolved({ state: nextState, poll })) {
            invokeAction({ ...action.action, state: nextState } as ExternalAction);
          } else {
            remainingActions.push(action);
          }
        }

        return {
          state: nextState,
          trackingDependentActionList: remainingActions,
        };
      }),
      doTrackLinkClick: ({ event }) => {
        if (event.type === 'linkClick') {
          tracking.linkClick({ external_url: event.external_url });
        }
      },
      doTrackMediaProgress: ({ event }) => {
        if (event.type === 'mediaProgress') {
          tracking.trackMediaProgress({ media_progress: event.mediaProgress, questionId: event.questionId });
        }
      },
      doTrackFinalClick: ({ event }) => {
        if (event.type === 'finalClick') {
          tracking.finalClick({ finalId: event.finalId });
        }
      },
      doClearTrackingErrors: assign(({ context: { trackingError, state, poll } }) => {
        if (trackingError) {
          invokeAction({ type: 'ready_after_error', state, poll });
        }

        return {
          trackingError: undefined,
          retryCount: 0,
        };
      }),
      doClearNextCalculatorErrors: assign(({ context: { nextCalculatorError, state, poll } }) => {
        if (nextCalculatorError) {
          invokeAction({ type: 'ready_after_error', state, poll });
        }

        return {
          nextCalculatorError: undefined,
          retryCount: 0,
        };
      }),
      doAddNextCalculatorErrorAndIncrementRetryCount: assign(
        ({ context: { state, retryCount, trackingError: previousError }, event }) => {
          if (event.type === 'xstate.error.actor.nextCalculator') {
            sendError(new Error('nextCalculator service invocation error'), {
              state,
              severity: 'MAJOR',
              actualError: event.error,
            });
            return { nextCalculatorError: event.error, retryCount: previousError ? retryCount + 1 : 0 };
          }
          return {};
        },
      ),
      doAddTrackingErrorAndIncrementRetryCount: assign(
        ({ context: { state, retryCount, trackingError: previousError }, event }) => {
          if (event.type === 'xstate.error.actor.pixelSender') {
            const trackingError =
              event.error instanceof TrackingError
                ? event.error
                : new TrackingError(state.questionId, state.groupId, state.lastPixelHash ?? null);
            sendError(trackingError, { state });

            return { trackingError, retryCount: previousError ? retryCount + 1 : 0 };
          }

          return {};
        },
      ),
      doinitServerPassPollErrorAndIncrementRetryCount: assign(
        ({ context: { state, retryCount, trackingError: previousError }, event }) => {
          if (event.type === 'xstate.error.actor.initServerPassPoll') {
            sendError(event.error, { state, severity: 'MAJOR' });
            return { initServerPassPollError: event.error, retryCount: previousError ? retryCount + 1 : 0 };
          }
          return {};
        },
      ),
      doClearinitServerPassPollErrors: assign(({ context: { initServerPassPollError, state, poll } }) => {
        if (initServerPassPollError) {
          invokeAction({ type: 'ready_after_error', state, poll });
        }

        return {
          initServerPassPollError: undefined,
          retryCount: 0,
        };
      }),
      doPreStartTransformPoll: assign(({ context: { state, poll } }) => {
        const {
          links,
          questionsIndices,
          poll: transformedPoll,
        } = pollPreStartTransform({ poll, questionsIndices: state.questionsIndices, links: state.links });
        return {
          poll: transformedPoll,
          state: { ...state, questionsIndices, links },
        };
      }),
      doUseFetchedState: assign(({ context: { state }, event }) =>
        event.type === 'xstate.done.actor.beforeAuthStateRestorer' ||
        event.type === 'xstate.done.actor.persistentStateRestorer'
          ? { state: event.output ? { ...state, ...event.output } : state }
          : {},
      ),
      doInitializeServerPassPoll: assign(({ context: { poll, state }, event }) => {
        if (event.type === 'xstate.done.actor.initServerPassPoll') {
          tracking.setPoll({ ...poll, ...event.output.poll });
          return {
            poll: event.output.poll ? { ...poll, ...event.output.poll } : poll,
            state: {
              ...state,
              links: state.links ?? event.output.links,
              questionsIndices: state.questionsIndices ?? event.output.questionsIndices,
              externalLogicRules: state.externalLogicRules ?? event.output.externalLogicRules,
            },
          };
        }
        return {};
      }),
      doMarkAnswerTrackingStatus: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll }).reduce(markAnswerTracked, state),
      })),

      doMarkInvalidAnswers: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll })
          .filter(q => !findLastInStack(state.answersStack, q.id).isWithoutAnswers)
          .reduce((s, q, _, relevantQuestions) => markAnswerValidity(s, q, relevantQuestions, intl), state),
      })),
      doMarkAutoCommited: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll }).reduce(markAnswerAutoCommited, state),
      })),
      doMarkAnswered: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll }).reduce(markAnswerAnswered, state),
      })),
      doMarkSkipped: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll }).reduce(markAnswerSkipped, state),
      })),
      doSetAuthState: assign(({ event }) =>
        event.type === 'xstate.done.actor.authStateLoader' ? { user: event.output } : {},
      ),
      doSetEmptyAuthState: assign(() => ({
        user: undefined,
      })),
      doNotifyDoneRequests: assign(({ context: { state, poll, trackingDependentActionList } }) => {
        const action = { type: 'done_requests', state, poll } as const;

        if (canClosePoll({ state, poll })) {
          invokeAction(action);
          return {};
        }

        return {
          trackingDependentActionList: [...trackingDependentActionList, { action, shouldBeResolved: canClosePoll }],
        };
      }),
      doNotifyDone: assign(({ context: { state, poll, trackingDependentActionList } }) => {
        const action = { type: 'done', state, poll } as const;
        const shouldBeResolved: ShouldTrackingDependentActionBeResolved = params => {
          return canClosePoll({ ...params, waitOnlySystemPixels: true });
        };

        if (shouldBeResolved({ state, poll })) {
          invokeAction(action);
          return {};
        }

        return {
          trackingDependentActionList: [...trackingDependentActionList, { action, shouldBeResolved }],
        };
      }),
      doNotifyWrongConfiguration: ({ context: { state, poll } }) => {
        invokeAction({ type: 'wrong_configuration', state, poll });
      },
      doNotifyCompleted: ({ context: { state, poll } }) => {
        invokeAction({ type: 'completed', state, poll });
      },
      doNotifyPassed: ({ context: { state, poll } }) => {
        invokeAction({ type: 'passed', state, poll });
      },
      doNotifyReady: ({ context: { state, poll } }) => {
        invokeAction({ type: 'ready', state, poll });
      },
      doNotifyNetworkError: ({ context: { state, poll } }) => {
        invokeAction({ type: 'network_error', state, poll });
      },
      doClearTokenInStorage: ({ context: { state, poll } }) => {
        invokeAction({ type: 'clear_token_in_storage', state, poll });
      },
      doClose: assign(({ context: { state, poll, trackingDependentActionList } }) => {
        const action = { type: 'close' as const, state, poll, eventId: nanoid(10), unsafe: false };
        invokeAction({ ...action, unsafe: true });

        if (canClosePoll({ state, poll })) {
          invokeAction(action);
          return {};
        }

        return {
          trackingDependentActionList: [...trackingDependentActionList, { action, shouldBeResolved: canClosePoll }],
        };
      }),
      doAutoClose: assign(({ context: { poll, state, trackingDependentActionList } }) => {
        const getNextState = (state: ICState) => {
          const finalId = state.readOnly ? next(poll, state).finalId : state.finalId;
          return { ...state, finalId };
        };

        const nextState = getNextState(state);
        const action = { type: 'autoclose' as const, state: nextState, poll, eventId: nanoid(10), unsafe: false };
        invokeAction({ ...action, unsafe: true });

        if (canClosePoll({ state: nextState, poll })) {
          invokeAction(action);
          return {};
        }

        return {
          trackingDependentActionList: [
            ...trackingDependentActionList,
            {
              action,
              shouldBeResolved: ({ state, poll }) => {
                return canClosePoll({ state: getNextState(state), poll });
              },
            },
          ],
        };
      }),
      doNotifyOverall: ({ context: { state, poll } }) => {
        if (poll.provideParentOverall && state.finalId) {
          const overallResult = getOverallResult({ state, poll, finalId: state.finalId });
          invokeAction({ type: 'overall', state, poll, overallResult });
        }
      },
      doSetWelcome: assign(({ context: { state, poll } }) => {
        const [welcome] = poll.welcome ?? [];

        if (welcome) return { state: { ...state, welcomeId: welcome.id } };

        throw new Error('Welcome must be defined!');
      }),
      doTrackLoad: ({ context: { state, poll } }) => {
        invokeAction({ type: 'load', state, poll });
        tracking.isStarted = state.startedAt != null;
        if (!tracking.isStarted) tracking.load();
      },
      doUseNextState: assign(({ context: { state, nextState, poll } }) => {
        if (!nextState) throw new Error("Can't find nextState");

        /** Берем answersStack из текущего state и добавляем к нему новый элементы из nextState.answersStack */
        /** Это защищает нас от изменений в state.answersStack после получения nextState */
        const answersStack = [...state.answersStack, ...nextState.answersStack.slice(state.answersStack.length)];
        const trackingStack = state.trackingStack;
        if (nextState?.finalId && isFinalWithZeroAutoClose(poll, nextState.finalId)) {
          return { state: { ...state, readOnly: true }, nextState: undefined };
        }
        return {
          state: { ...nextState, trackingStack, answersStack, lastPixelHash: state.lastPixelHash },
          nextState: undefined,
        };
      }),
      doInit: assign(({ context: { state, poll } }) => ({
        state: reduceFirst(poll, state),
      })),
      doStartGlobalTimer: assign(({ context: { state } }) => ({
        state: { ...state, startedAt: state.startedAt ?? new Date().getTime() },
      })),
      doTrackStart: assign(({ context: { state, hasStarted, poll } }) => {
        if (!hasStarted) {
          tracking.init();
          invokeAction({ type: 'start', state, poll });
        }
        return { hasStarted: true };
      }),
      doWelcomeClick: assign(({ context: { state, poll } }) => ({
        state: reduceFirst(poll, {
          ...state,
          startedAt: new Date().getTime(),
          welcomeId: null,
        }),
      })),
      doTrackWelcomeClick: () => {
        tracking.welcomeClick();
      },
      doSetFinishedAt: assign(({ context: { state } }) => {
        return { state: { ...state, finishedAt: new Date().getTime() } };
      }),
      doTrackImageVisibility: () => {
        // TODO: refactor by moving pending events into contexts
        if (hasPendingEvents()) {
          const pendingEventGroups = resolveAllPendingEvents(MEDIA_EVENTS.COMPLETE);
          for (const { questionId, pendingEvents } of pendingEventGroups) {
            tracking.trackMediaProgress({ media_progress: MEDIA_EVENTS.COMPLETE, questionId, ...pendingEvents });
          }
        }
      },
      doMediaProgressChange: assign(({ context: { state }, event }) => {
        return {
          state:
            event.type !== 'mediaProgressChange' ? state : updateMediaProgress(state, { ...event, touchedBy: 'user' }),
        };
      }),
      doMarkInvalidUnanswerable: assign(({ context: { state, poll } }) => ({
        state: selectRelevantQuestions({ state, poll })
          .filter(q => findLastInStack(state.answersStack, q.id).isWithoutAnswers)
          .reduce((s, q) => markUnanswerableValidity(s, q, intl), state),
      })),
      doInvokeDeeplink: assign(({ context: { poll, state, trackingDependentActionList }, event }) => {
        if (event.type !== 'invokeDeeplink') {
          return {};
        }

        const action = { type: 'deeplink' as const, deeplink: event.deeplink, poll };

        if (canClosePoll({ state, poll })) {
          invokeAction({ ...action, state, poll });
          return {};
        }

        return {
          trackingDependentActionList: [...trackingDependentActionList, { action, shouldBeResolved: canClosePoll }],
        };
      }),
    },
    delays: {
      REMAINED_TIMER: ({ context: { state, poll } }) => {
        const screenTimerInfo = getScreenTimerInfo({ poll, state });

        if (state.startedAt && poll.hasTimer && poll.timerDuration !== undefined) {
          return Math.max(0, poll.timerDuration * 1000 - (Date.now() - state.startedAt));
        } else if (screenTimerInfo?.timerDuration) {
          const timerCreatedAt = getTimerCreatedAt(state, poll) ?? Date.now();

          return Math.max(0, screenTimerInfo.timerDuration * 1000 - (Date.now() - timerCreatedAt));
        }

        const oneYear = 365 * 7 * 24 * 60 * 60 * 1000; // i.e. never

        return oneYear;
      },
      AUTO_CLOSE_TIMEOUT: ({ context: { state, poll } }) => {
        const final = poll.finals.find(final => final.id === state.finalId);
        return final?.data?.hasAutoClose ? final.data.autoCloseMs ?? 0 : 0;
      },
    },
    guards: {
      isReturningFromAuth: ({ context: { poll, state } }) => {
        if (!state.questionId) return false;
        const question = selectQuestion(poll, state.questionId);
        if (question.data.type !== 'POLL_AUTH') return false;

        const authAnswerState = findLastInStack(state.answersStack, state.questionId);
        const haveChangedAuthAnswer = !!authAnswerState.answeredIds?.length;
        return haveChangedAuthAnswer;
      },
      isAuthenticatedWithSkipAuthenticated: ({ context: { state, poll, user } }) => {
        if (!state.questionId) return false;
        const question = selectQuestion(poll, state.questionId);
        if (question.data.type !== 'POLL_AUTH') return false;

        return !!user?.id && !!question.data.skipAuthenticated;
      },
      isNotRequiredAuth: ({ context: { state, poll } }) => {
        if (!state.questionId) return false;
        const question = selectQuestion(poll, state.questionId);
        if (question.data.type !== 'POLL_AUTH') return false;

        return !question.data.requiresAnswer;
      },
      isAuthenticated: ({ context: { user } }) => {
        return !!user?.id;
      },
      isNotAuthenticated: ({ context: { user } }) => {
        return !user?.id;
      },
      isOnAuthStep: ({ context: { poll, state } }) => {
        if (!state.questionId) return false;
        const question = selectQuestion(poll, state.questionId);
        return question.data.type === 'POLL_AUTH';
      },
      isWithAuthStep: ({ context: { poll } }) => {
        return poll.questions.some(q => q.data.type === 'POLL_AUTH');
      },
      isDebugMode: () => !!isDebugMode,
      isWithGlobalTimer: ({ context: { poll } }) => {
        return !!poll.hasTimer;
      },
      isShouldRestoreState: ({ context: { poll } }) => {
        // проверка useSinglePass в первую очередь для обратной совместимости со старыми опросами
        return !!poll.restoreState || !!poll.useSinglePass;
      },
      isPassedPoll: ({ context: { state, poll } }) => {
        if (!state.finishedAt) return false;
        if (poll.singlePassExpiry) return new Date().getTime() - state.finishedAt <= poll.singlePassExpiry;
        return true;
      },
      isWrongConfiguration: ({ context: { poll } }) => {
        const isTimerInvalid = poll.hasTimer && poll.timerDuration === 0;

        const isRestrictedTypePresent =
          location.origin === 'https://media.tinkoff.ru' &&
          poll.questions.some(question => ['POLL_AUTH', 'POLL_FILE'].includes(question.data.type));

        return isTimerInvalid || isRestrictedTypePresent;
      },
      isNotServerPass: ({ context: { poll } }) => {
        return !poll.useServerPass;
      },
      isServerPass: ({ context: { poll } }) => {
        return !!poll.useServerPass;
      },
      isCompletedPoll: ({ context: { poll } }) => {
        if (poll.isCompleted) return true;
        if (poll.isCompletedAtTime) {
          const currentTime = new Date().getTime();
          const completedTime = new Date(poll.isCompletedAtTime).getTime();

          return currentTime >= completedTime;
        }
        return false;
      },
      isCanGoBack: ({ context: { state, poll } }) => {
        const goBackIsEnabled = !!poll.canGoBack;
        const hasPreviousAnsweredQuestion = state.answersStack.some(
          answerState =>
            answerState !== last(state.answersStack) && (answerState.isAnswered || answerState.isWithoutAnswers),
        );
        const previousQuestionIsPollAuth =
          findLastStoredQuestionSafe(state.answersStack, poll.questions)?.data.type === 'POLL_AUTH';
        const isFinishedPoll = state.trackingStack.some(tracking => tracking.type === 'finish');

        return goBackIsEnabled && hasPreviousAnsweredQuestion && !previousQuestionIsPollAuth && !isFinishedPoll;
      },
      isUnauthorizedErrorWithAuthStep: ({ context: { poll }, event }) => {
        if (event.type === 'xstate.error.actor.nextCalculator') {
          return poll.questions.some(q => q.data.type === 'POLL_AUTH') && event.error.name === 'UnauthorizedError';
        }
        return false;
      },
      isForbiddenErrorWithAuthStep: ({ context: { poll }, event }) => {
        if (event.type === 'xstate.error.actor.nextCalculator') {
          return poll.questions.some(q => q.data.type === 'POLL_AUTH') && event.error.name === 'ForbiddenError';
        }
        return false;
      },
      isAllQuestionsSkippable,
      isAllQuestionsSkippableAndAutoCommit: a => isAllQuestionsSkippable(a) && isAutoCommitable(a),
      isAllQuestionsSkippableAndPoll: a => {
        return isAllQuestionsSkippable(a) && a.context.poll.template === 'poll';
      },
      isAllQuestionsUnanswerable: ({ context: { state, poll } }) =>
        selectRelevantQuestions({ state, poll }).every(q => findLastInStack(state.answersStack, q.id).isWithoutAnswers),
      isSomeQuestionsChanged: ({ context: { state, poll } }) =>
        selectRelevantQuestions({ state, poll })
          .filter(q => !findLastInStack(state.answersStack, q.id).isWithoutAnswers)
          .some(q => findLastInStack(state.answersStack, q.id).lastTouchedBy !== null),
      isAllQuestionsUnchanged: ({ context: { state, poll } }) =>
        !selectRelevantQuestions({ state, poll })
          .filter(q => !findLastInStack(state.answersStack, q.id).isWithoutAnswers)
          .some(q => findLastInStack(state.answersStack, q.id).lastTouchedBy !== null),
      isWithWelcome: ({ context: { poll } }) => {
        return !!poll.welcome && poll.welcome.length > 0;
      },
      isStrictTimer: ({ context: { poll, state } }) => {
        if (poll.hasTimer) {
          return !!poll.timerStrict;
        }
        const screenTimerInfo = getScreenTimerInfo({ poll, state });

        return !!screenTimerInfo?.timerStrict;
      },
      isReinit: ({ context: { state } }) => {
        return !!state.startedAt;
      },
      isAutoCommitable,
      isInvalidAnswerOrUnanswerable: ({ context: { state, poll } }) => {
        return isInvalidAnswer({ state, poll, intl }) || isInvalidUnanswerable({ state, poll, intl });
      },
      isInvalidAnswerChange: ({ context: { state, poll } }) =>
        selectRelevantQuestions({ state, poll }).some(
          (question, _, relevantQuestions) =>
            !!validateAnswersOnChange(question, relevantQuestions, state.answersStack, intl),
        ),
      isWithImageFeedback: ({ context: { state, poll } }) => {
        if (!state.questionId) return false;
        const question = selectQuestion(poll, state.questionId);
        return hasImageFeedback({ question });
      },
      hasFeedback: ({ context: { state, poll } }) => {
        const group = state.groupId ? selectGroup(poll, state.groupId) : null;

        if (group?.data.autoNext) return false;

        const questions = selectRelevantQuestions({ state, poll });

        return questions.some(question => {
          const answerState = findLastInStack(state.answersStack, question.id);
          return hasFeedback({ question, answerState, template: poll.template, sendError });
        });
      },
      isReadOnlyLastScreen: ({ context: { state, poll } }) => !!state.readOnly && !!next(poll, state).finalId,
      isFinalScreen: ({ context: { state } }) => !!state.finalId,
      isInstantAutoClose: ({ context: { state, poll } }) => {
        const finalId = state.readOnly ? next(poll, state).finalId : state.finalId;
        return isFinalWithZeroAutoClose(poll, finalId);
      },
      hasAutoClose: ({ context: { state, poll } }) => {
        const finalId = state.readOnly ? next(poll, state).finalId : state.finalId;
        const final = poll.finals.find(final => final.id === finalId);
        return !!final?.data?.hasAutoClose;
      },
      isCloseable: ({ context: { poll } }) => {
        return ('closeable' in poll && !!poll.closeable) || poll.template === 'dialog_webview';
      },
      isValidUnanswerable: ({ context: { poll, state } }) => {
        return selectRelevantQuestions({ state, poll }).every(
          q => !findLastInStack(state.answersStack, q.id).errorMessage,
        );
      },
      hasLoadedExternalLogicRules: ({ context: { state } }) => {
        return !!Object.keys(state.externalLogicRules ?? {}).length;
      },
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAYkwBsB7WMAbQAYBdRUAB2twBddL8WQAHogAswgOz0AdACYAbAE4AzLOHz59AIyiANCACeiALQatkxdOEL5sgByLhF6QF8nutFjyFSnAE5YA1gDKnOicAK6wAKqsEKF0TPzssFw8fEiCiMrikmIa8hpi0ooArPTFNsViugYIhsWy0jn00jbyxW309DY2zq4g7jgExCQEAG6U-mAAImBgrOQE-gzM6UkpvPxCCIpKimbm9OLlwop51Ub1jRItsvTywho2h7IubhiDXpIAJARcJMuJDjcDbpLaGYSdGxmWRaSw2MT1NSyc61PKNYqOeStMoHRSvfrvTzEb6-Tj-DQrNhA1KbIyKMSnSQqWzSMQPR73RQowzSDSyMyWXkKDT0U7SYr4gZEoiSABmYE4HygAEEwpxsME4pIqOgIAQoCQILwwJIxhMTeg1RqQpwwAAZSi6sA+AGrakg0BgkomMwSSqWeiyRTNYQokXySTlHbmeo2WQYmzCSWEoYy+WKzwqq2a23ax16-AG50+Sg+STzUKy0uoSSW9U5+3552uqnJYFpT1GCGtJkFIoPeqyWzFFH2fkVPnSUX5YqWYp4vpS1NyhVK1X1m0mnUFos+EtliucKs+Gt161xB1Ol0UwFtmmgumWMSSEwVNlsuMY0d3MwY9TqO5SixF5FxTT50zXbNN0kHwFR8PR9UNY1TXwcZJlrKCLybF0EjdO8PQyWp7HaSRA1FOR5CKDRZxRYRukjQp8ghAcCjEZMPGXCDM3Xc9c1g3wEMLEhi1LctyErasMI3LCrxbEA1nbWlagcecmR6Q4xDEfJpHuFF2gjYpjm6SoGXUDR2I+YkuP1HiG0kESfBIfj4MvHdbM3OSFPvTtlPI0jNHuKcSnUEd9EQdooTKRNbHhZjLAs6Vvm3SB3Lif5cNbdYO0IwxTkUKEbDyEU5GnepuSnYpJDorQikUOrNAcXo3g4z4vmSiBUttclKXk91srBcV1FI+x6FMjpqPKsoqsKhw6vqmqmoJFriTapsOswrraGkHqvIIgadOEfZynKXJNHsKowtqCrppqubgwWhLl1g2BOFLfUADES1QAAFShyHIWBAi4MBglLdAYCQwgULQk1WGdZIXrAfBOAbAAlOBXtgnCdr6pS6lnKqp3yU4FE0s5LrkSQ2k0h57gaOwFEez5nsxj6vt+-7AeB0G-AhhyxIkk9y3h3BEeRtGMdLZsMt6-D+qMRiXy6fTOh03luXDGRDInWxNPMBkmeJUlAmdUZnW+9BYFgDnyEhk0zXQ43TfNy3rb+8hPNxh8EAKSq+SxJ9ZEKRwLpqKRSgxc66tJ6wukNmUnZ8M2fAtq2beEvdRMPY8a0T5PU7d-7PblvG9mp+c2RaNkShKFEiikaih0TYpqMOJQQOayyE-wLgTaTl20-dgBRTPHOcvQAEke5R52U9dm3i6y0v40jbong0jF4yHOu6NXkw-WFIpGdA5aZRZt7CwAITAY8wE6sA7eh81JAAIxvqX7-Rl6pex28l+94whl+T3EoqKDQ9JhAV3KnkJkDI8jnUKkHeQ8cYKSx8Pqa+t974Z33ALI8kk35YI2mAL+mNpY4xLgAvIMJJCtCxPOeoJg4QoihGICooglAnHjImBwKCfjT1wOgBYAAvSA6dF6KW9oUKEJQETwhmmArkl1jBqDMOA+os0HhBgxHw0kgiRFiPdt1P+kifJsP5PrfKBQ8j-nkNyTSOQg7xg0LyAos0UHbn1EPAQtofD4CEQ6KAuBMCozCOQOAj8HYmjAD450-jyCBOCaE8JsBXLkJMd5Qi4Dmg5HpK0Co9JyjdFoodKws57jtE0E8PkHj8xeNiX4gJlAgkhLCRE-m2dJIxN8fExJrSUlpN-nhf+PkRT1Cpk+XIrd7ihzpI0VoLiQHByDCYFBvh0D4FgDnRCEjMlbEblIa4dxeQtAqNIcqvJaEFBnBpZW3Q1l+E2dsoStAbzDNMVkypkYuhtAsPOGEE1lEOLEE4nYIKmFqEWkuVq6ynnVkgOlChIzCJTkDFTIBTcLAIjKsomxaiSgqH7HyEoUKwIrVhVs+FEBjHvL2YgKcEIXynGxTsOMeS67Pk0pUTQ4K8jdFFCg2A6BRj6gbAAFUoDzcGD8jRQyiaaVAqBICCNtOQPQ0RYi2ilTABsuy9r0oxFIeM5hfaaCDCoDlpFWQ8utSCwyILBXCtFZuCV2qH4dPEvgoWuBFXKriGqjVcQ3W6plrteWCBpBUV-FpfKZzOhKJqKyK1Eg+wSDtZUDuS0u5JTWoijJ+qI2ij2EfQ4Jy26hkuhCRoQd14gJbk+Ph7UaWZQ+VsIoRRSIAWaOA8Uo1aI5JrZocBdg2iiD4bBXUeg820oLTpW4VUwWBh0vYOQNhRxPBfAyGOqhOijXHWASdzbZbIq2PYHYtCtJqBBfcPIlh11SFOHGfI8hCgNDaBKE+2avgTogFOraSLW2ZFsMWwM+UQUNAZOcy6+UH2PGFC+uQc62KfsSt+g9v7-iKAA3S7YHQcg7rUC3EUyhQo1Bg2o+ESgOQFGMiggA7mAcgmBKBKpIAxpjLGwAAGEFiYCWKGr2PliJGpcT0GEdFLAaBRPGCMIZ9a3HEE8BcndEpQCoC-IRYqfXOgAOqMeY6xtTlANPkC00qnwmofCcD1eG+kmgZA2JKIVSO0m2Q5AZOYW42lThJhQ8uAAjmEDGqRYDfFgHRrgSpp0tpw9GDQkZlDHPUdeuub5IyNSHC4sDpLT6SEC8F3goWvjhci5mS+ARovHsA9sFoxagwOHjKUBEdjoM8qprsRdWiP0qYC0Fl6IXJCwEwLBJGJB8v9d4O9SgmAIg2aUjscUAo7DdCvQieM-bxkqFGnVP8mlNIoPG+2ULQ2Rv4BIEqvU6BvoligM9WAXHsAbJgHN720Y9hDlsNRBkFccVkd1mpE4inhQ7F8z1z4h2BsndmGdi7gjrvNLuy9oT1hKq5HhCmoU4paKrqpiSmMEgVDmT8+DvrR3BvDehyQBY+B-A8eCfx7DBbWXzIsD8p4b56Trocca-KDwHAnEqAd0nkOKejaR4RB4SbqL-M6F53IddhRUxMLGkUXRCjdazYlCHhXyenaPWGpSfPUdByKMdIM8uKYgpZ9GBQRQ4yHCFwVzZuvoeSBwE9hFGm+Pi9PeKQ6GJVCPEg+GUcdV4uBjaMFSBhTlOa960747ov8Bu8e4WBFsB-C4FYD7+lopDrUROByUadg+SpZFDkSolQdJxmcchsHxJtfO6h0jFPHvqU559iyMwIYJPS9GlJ6DlHfxPrfe0ecjuJtN6T63tPEBvijCEbgTVOyBOUNGVoeLfI4zXE-CHim+R4tN27SUMobCJ9k+b8n93s-5+L+Xy8t5MWC0mEqL6bWcW7h0Q5W5yNiy2H5WsCHHPxF1OxnxgDn1gDCBflQC4BCBfnCRIA2XC3SRnXDXATcxcXjAcEOGoRODrgsH5HIlmir3nBqWJwb2Fx10vzAMgEGygJgM4DgPCUkDCHwCoD4wRWv2e1XxPUQAZAfVZC5QWzkQTXpScXRVJkvVATrzjxJwTxdxby4NoMgOgNgPQHgJNFYPYMmGpUoHVHhgew9w72MiZXKDyCjEDE0gVxBQkJfSkKnBkOhQoPkOoKUIgPoLUI0JYLYOmx0JIFlFwHCWiG3A71nCkBuC6Eo3uGMgVyDGmlLROhg1nGAKoOnzcLoNUMYPUOYK0N8PT0z2zx4Oq3AQUBfHFF5AzU-EODrkeHiy0lGgaB2BbjnRSKn1APSJUIYKYM0J8I4PbyKJwxcUeCuT5EYml2qjrh2Hi2xBJSeHKBfVaMT3aNT3AIyK6OyJ6O0IRQEBei1HQFlF8WQFRiHgAFllQJ4AA5IeKYAAfTFQnhOKHlRlICcJlEbyWNdw6I8KyK8NyL6I70DyVxhHUB21ihqK6CpmLz1lED90WIUKvxWOUO+O6O8K2OpR2Ogn2MOOOLOMuOuLuIeKeJeLJTeMoLaM+MRPcMyJRL+J0NeUZ3DR0mfGOQjhinrQKEmIhNuGDC0iCinHFDhNcMpLC2RIRQ72l0aAhFGnaBcSqQqB3hUCZBhPqnnCnEzVeLyzJI+MUOFOK1FPb0fyqxwyChkRUAcHfErgH0TUsFKWVODFVMDEFLSOFLGDv1RLyOpSUI7xvRfE-GUAkCeEJVHE1jok6HQKxARBy2zXePhJoLn1dIWDn1pIRT0OwAMMRO9PPXMAAjZBMDblmRqyTXYXKFGnbmoidOWLb2hjdOTOpQCKCPmHzEzMOmpiRCUH4IeFDxKG+UgRvS0FyHpArIpKrITKX3dL6JIAEmVE2QYyGSfzQJXUjDCMU06FyB0jrmphkCDB6AxFnAxEcJJM1JcOdJHNQhrN6L8IxL2IOOdCONOPOKuNuPuMeOeJIA1JjKFNPIX0TPHLpIGOf3yj9mjkOFOHnAcDXVazaByGolXV5EDCDCHJ1K-PPLRJICvNzCxNvJxIfPxOfKJLfMPI-JPJv1HKTIvMgHpPzQXMonc3Ii6H7DsFD1FCtUnAhCDCrkQoROQp-NQozyzw70jQcCqluFuBlxOAgrI0-wcxbmsGjEKnAU4skHWQnK9P-MZK3loRMhUHksU1EO2FnGZJMBATphaAFXINJOPNAOUr8NTPTKMLUqUgsD5CaCeHqLtTsFIyAx-G7SUyDnaAsEUusoRXrLAGCKbIcu9nFETC1ksCQR6H3mRGg1JiZB2E6BfV3IqA13fK1NjKCupS9wZyovmz90jAsHyHhBNTuFHBUgSy8xI0ojcUCr8AnKnJnJQPnKUlvWARFEKEjNcUgVD2itMu6GsQazjCaoCHyP4oip8gIKhCD1qK3z5CDFD3yByGAiLRhGaFZAmonIEpbnCMjTkSDGzPW0rTIi1kohBWVK6FB1kOcMn21OTzyv10ExRRhD2GDCDg5FUCOtHFjgmRaEjUcBL12r8P-SKsirUEOk0DuBOt5z937R6Cqll1GuOgNnMqPMetyuasvN2IwpvJ8DvNxMfIJJfOJNyyIqstxoopmvevsArzE0Kh0ksL0q+saAsFEGCn5PEFj2ysstdxevQotEJuJpwqfMJNfP5uxuoLysotQMcp3LKK2xGifHlOg3FGfEgTkERC4QhAPMppytltxq8VHicjgj0G+lwAEEYxNnwAgHFJmS1jKpOUKjjDDEMgjA5H0jXkcDuulov2n2sv1DdyERm09X1AuO6UiVQmfkIB8S4zDrCVCFLAEsgRkRMCnDjFaGUHsA9volZDUBry6DHCysIqNqDpNsLFDqY2Tu4ELCjp8RwSzk9RzkkHjs4ETtrs9VTrpv2U5G72jG1q0grRqEWXzzIjCJsV5DBpDswCTojobujo9UFhrA7q7vDpTuvAZMcvq0hL7BhC2wsDDFZH90iKbnVwUBsFnurvnu7tCEjuXtHjwTbvXoXq3ohoVte1V1oXyirnnDsGjjDBI2hFcTYT5UDCJ3rwsplsroCBDpoHtv1CtptoBhjphnLGtttqRggHaqNNnUDMjBfQKHgo0U8ojVZChBClsCUHnAqTLsNoFpb2DursQZ3BQcY1gGboPFbsklYCwfIDttwbnPwbQMokqjaAKAUC6CkfEDDG+xkCrTYRaCOHMBvqgHbu6Q3rrtLBHn3HNoEkbs7vfsxg7zygkZ6DUD5RvSKA9sMk0t3KPjZSgfupgcDupvgerrfvvsxkiH8StDelEQgD0dLHNtlGemwB4iRm4HnvbAEviwOlZ3yAZEKjaESpqEMmcrE3TvaFDINujIrtAKQNnNoPlEgAKpIA7rMc1rMGW3N1uDmPIeXU33pF9jYX+TkEUuKedFKdmAgAqYKuqcZTSqaOsC0AtX3wZH8jRzZGSc1q6batgjnzKf6Yq2qanFoREuBoeAWz0puAjF5HZGblVgYYKaYeT26aWZXHKbWcNIN2-o3QP0jR8yvqac0kikL2KjhBnsxqptd0ud6Zub43+G2khqEz7EcWAxaH+VAo5Q2frXhGoh6GCj5vLvOakmwBJGnkq3udGQhFk2jjUGJhaCtPCjSt9GoQKW4VOa10Kf+atCxb+Hlo6u9iRYbnqD6seDdtHvClYlIm0RhGXXMCjNpfRbPEZbJE-pZdGXjCkDUGVgaHFHsDaD0mDEOWHXKqCjqi6YZdYIBc9N4HrqCyieRmCTiAdr7sQGntxxjQW0AK-Eun3MqjogJz-shWQV+bpZb3Fb1cWfTxCCsxNZiYft4HFJbkimnF5BLzok50daAgXSozV3kppfj1gaKd1f8T9epT4tYCDbNbictYoZ9EMjOiVeaCaz0ksAMl3LaFaC3VRcYbTfpfVFQQEkQl2MDatGifzdSDMbsFx1+vxbz0Mlom5KZDGYkEomDGUB1ZbfHnbYKLzdid7cLfUBhrs0gTYWsGj1omUAOe21KETEOBTbkKbe9YZYgDCFQH4cLAbEwQ-itHQefkIQfekltCmCvbhhEdxcIjSaqkgRtKdc5D0n9naxPxN0DI9egaxvcebcxcvevedTiHvdgh4m4ZfoIXflQ+IQ-evbwZ-a2BN1IhgrJlFDORawyZJiZB5pOGsCxAbbObPYuYZa+GY1NfwDCBXx3sipwKpgUHuimK0kKFomOjMF+W6BOC0njFncxa+Eua47BZRXMD2CHSYnMDEy0Fohoy3Lol3IaLyBk++H4jCD8QU6-plbiMyvg0bj21HZ9BMAaBtIOHuU9bFZY+M9M4f249GR5NKtzLo9GlZHSZEFEomTlwPysEUt9eQL8C8NJEqe6TMfsChAUHqH0k0iHB5YQHEumLuDqmebARFdTdg5b2i9nI2JQj+Fhyuxuzu0MLTzMdlOI9aascFEqBKQJiZPpEy2JnydFaY+8MuYq-i8GcLf1kPxBTy+MkqD0pOAsHwxAs4W4Si8zZi4q6+G-KXwfpeTG5UGdf47DK3a3mxwcXQIeHhHNTKBW6G68I27v224NGZdEfmxgt-sncJz5HtRO+fDO6D0u5PYepK+TxiX4aWZIBzYEuZFoV7SxS2s3jhYjEhQ4WIyDj6+K5ANd2KwKJvce8LZ1oSc-yLWfR2DrlnElMk1P1OADgB7TAICEX8Lp-IDp290LbokjSIe2vNy5Sy5KhkUgRIKsAKAY8SgCPiQlZxbeq2AkykHEH560HblODrkDChEjSo3gSQTR-AkZ-F6e4I5EEyqqjJlzJMGolrgpkEqVnt3FH3O0pQVF6ER19BfM4l26GrVuC0gKU6F+3pXsHi2XO1ufRhDt+14oA4ELHel+FgDTPRPxpFsOOVEiDFQAHkbiuM7Qk-Agh4yah4k-E+Kbs17fyA3cqBkhw-I-o-QiGVfQ4EnMY1SWI1H0ZBYoshzv2m+FQ-S-cfvOJcA9SrKGQERpyHoxCYGE2mtAwL91v4lmUO75H2O9DAX1ZNnnlc4aRKNZVFzHZw5xr1yhJ+yEIAZ+0PdfJeFYGbeT3LDJVALcagVEIxN-RB4wd-r7Ma0Mp-IAGwJe18coTVO1MnzExi9KPIQ4FVAqRfVWgMIeoHvylgQAP+x-L-qekR4VVb0MJVoHIEmiHRVA0eZXmM3tQuA+g+ASgLg3gDpBXiinMEIDgjAsgNIWkcetyGS7+RgS9gLmvkl0TTwyBXYDkEygaz1B9YuQchiokqhAJjoc6bWAFUxrWRCw98dgcpHuhqQGUmkbSLpEugIhN0IoLQMGDaBLI7eq4biMQjzC6h9Q0g8EFJSfQuJBC6gNAZdEnCwJAIB+ZiJpCK7gQdBNkPQfO0LBGCh6VUBzvVUOasgUQVuWBOJl97WItI2gjMC4LfbRJR4Hg7dtCAPyeZZWBZKtvyxtycg1Yz-aDqtCdDrRIhHg0QKwjiiBhlScjZRPyTKLbViWeQXICgnPjoJw+7Md2FzC1SYxpURgsZMAltwkNTgfIUvFYIbiNZ40MbC7uPkxp5wB4hccgEYNZB7AQU9RKcHBmUDBcEAKvIOFW20Q0YFKow6eH3Hzjzxh40Q53oRyoYIhRAMJSwPlAaC7sZhirWZnCHVz+1DytQjBFh1n65DDhRgfkpVFebREngkCaoqUOyBsohWZQR4JAMxp0YSwhYLjLwACJQATOIbbKHryIjihD8JQblMrzKBLCUutgQCIGBbgllHBxIAzPMAVCGJ-oUwlNJunOjtNyqBZbEU8A6DOISymw6DqwFdhkjJh7wn2P6ByCnID8hqOQGzXnQMg5wwYVJtyVYEpBF8QTG2NINKAc09sfIAcFvCy5ADGgGwgykGEOCn5akBgwsN4h6RNIWkySOANIKwL7ARQQcEMKWgkpWsZAvIBbNbzxE9DNexIClM8igDSDwEpgYepk3KTyjJo6o8BpqODAQhBcL-d0VSi9GJh4s4gWSuIDKCVBGKpQnJBqPEBaiwxromUEKhFS3sXUkqFoTAGkGq9Do4GGNPKOaAqtSh86UoKJU-CJh8gDuF-u1GLGaBKo-JMUaoBzq2Nzeh1EdCgN3LlkX+P6GoNK3erAD9IDQUDDVFtHbAbCcYkZu+h6H0Z9MnGaQcqzd57tRQpQb0JW0IKUNC65qcUOqUPJGYTMZmXTKuKVTrjFcZMB0QQQZCVsZEqsCQAcFS7C90ehWIwfCE7RRU2EiLSQiiADhMgyIkKQ1CoA-GnsycxWCLOEPcFcixw-ucwJLh3JBlLcbmGtDG1yDvMoOrjGDgNhgmlYMEAQdccPx5TtxJuNwLLsp0aBkd60pQUQO0E4oxDfxJ0boLkEAl9DCgSsBTGGNryKU3CrY8POI0xHGQN8BZeBGXAaLpCYoCxVzgN3SJ3dEyD3aQQiD2AASNBarPERuWYrRwbE7QA4NYAEnClOinhcJMWNUCsTRJE4B4AWX5KxjRoak0CpUAM7ySgecZNYmZK3Ael1xGzKoT9lFB3BZxDoh9DwiKFjg2QNPfCakUrI35TJPxHIuRQgByj7gL4duH8nYT60d49mKNkcAKQLYTxjbdyYpPim2hkpXI6ZO5hnBVxMsDrRNPVFIhSd6K6UyCYDwx5IUSKZ5RMl6LRDd5hQk3ZiJcNawQkKiynNinBUJFuN2pXFTqZtzIpokvRrOPqckwUzPAoMZGfeGlMeBFoJMuTYydxTHILSKpakxqWrjkCEpFe0GTaUVCUzJdeyDwoqdNKUo01ypY4ttGvHcxs8CUtDbeNBj-Bbl2g4KR9JGnUYhMfAQkxHgiEODs4-6xSKwcamGicgxo1gRMOo2kFUNRokuTOnYGaB6UX8lAsiGwl5q3VCpjHdySww0Z31N69dKAEY2LFgpSIkmZvp0C4RhhIEhBIdBUA2qURcJAdJ6RTMGw4NkGAjYgW9PpSA5N0-PdXK0C5ryNLJKgF9DuXUhVD1GmjBOiY10YHCxZ2wTgYugAgBh0cbM6wP5GIwKCuEjpNyfzKroaNvG1M0sH4zPCBNIAYMuUcAkxlDFRMwYHsWPWSpX16QaaOqGjgWYxdIAxY7EIbwQyjEzUdkmUhRiYizgPKWY6KeSW9ZZtrmqzPjMWKUBQg4xhQKOaMThaVRUm21L3ghlalTSYpcHUiRIw3FDtiI5DBwOXn3gzRyiDIJOX83PYtt+EXAL0emPawl5pEuQaGnpGrzCV6QygeSsXkM5lcemr057qy0AYV57g0eeSlpBHlYgx5W6SeWfktmVzO5mLNwZ6IqlVIqo8xWVncHAQUcQuGzZLtGCtx1ReZaLAbuKwQ44872LwniGaM6BKkYxYCbWg4D0hNZyh+tObiCnLnJynqGLVBHqFgiYAaZPU+kJukuAvoSMjWPSF0AfSvjEMoYwcrvJTnMcu5bHI1oYK5FEwCoWISPKIHAa0RmsVMEUVY2xntyvWBC2TvJ3gnazUU72WXiOgbFvhscL6XsFyhChDzDOaGcIJ5yPnazlqpSL7K+B0jbTZxRwSGcqwPyiYmF6LGebF3MkITAuzXIeUxAaDtdK0nA83LzU0EwVruizYbmwIQmcCC80cAFIUCy7iUqGIoJ4EUEhTSc8FkCzRetzmkqSEJLcfkLcCiplkJOu7SyeGGeCiBvRhkRSiD1wBLNix1cUiGyncXb5lqqWAmIiz5C3VsCGQvCR3OTxY8s8OPCyRUAcxC8XJKCjkhTBhDPgUZpaNnpQ2D7xJpB+QwgtmVyAOB6Os48wodFOQQTXE9CVpQ727mcB2lYFYaEyRqi9KleDwHIMZH5RKjuuoyovqL1FjR85RgoiZHAkBy4yr5EafIbQmcxDF2Z+2cQSHxL4fRy+ocrkZHH5AOFuWWQEnub0uSc1RQzWEti4w1Ksdrl7C+eT5HyFSAYx7EiQHRAUDSYFRvVCoEFMhAFKNSHfO5drMgTDFga1jFbO7UdZuZ-ShUBouYkeA1D0MSfNgqOMBUu9oqKyIcAiGsZLC+w72W4HDRzqKyEVh5Y8C-CXy4N8ALsrkScDqhENq8bIHEeAhPpQV6EoZc3PpCimv99+h-K0G0Pri5J+edwHEDWnX4HNEwjEK9JUF+RQClmDYIwf8lqZk92JM0NQIGM3TOZ0xoYsoL5hcBAA */
    context: {
      poll: originalPoll,
      user: undefined,
      state: { ...defaultState, externalLogicRules },
      initServerPassPollError: undefined,
      nextCalculatorError: undefined,
      trackingError: undefined,
      retryCount: 0,
      hasStarted: false,
      trackingDependentActionList: [],
    },
    initial: '$init',
    on: {
      close: {
        target: '.$closing',
        guard: 'isCloseable',
        reenter: true,
      },
      trackStatusUpdate: { actions: 'doTrackStatusUpdate' },
      invokeDeeplink: { actions: 'doInvokeDeeplink' },
      linkClick: { actions: 'doTrackLinkClick' },
    },
    states: {
      $init: {
        always: [
          {
            guard: 'isWithAuthStep',
            target: 'fetchingAuthState',
          },
          {
            target: '$loadedAuthState',
            actions: 'doSetEmptyAuthState',
          },
        ],
      },

      fetchingAuthState: {
        initial: 'loading',
        states: {
          loading: {
            invoke: {
              src: 'authStateLoader',
              id: 'authStateLoader',
              onDone: {
                target: '#(machine).$loadedAuthState',
                actions: 'doSetAuthState',
              },
              input,
              onError: [
                {
                  guard: 'isDebugMode',
                  actions: 'doLogStorageError',
                  target: 'error',
                },
                {
                  target: '#(machine).$loadedAuthState',
                  actions: ['doLogStorageError', 'doSetEmptyAuthState'],
                },
              ],
            },
          },
          retrying: {
            invoke: {
              src: 'authStateLoader',
              id: 'authStateLoader',
              input,
              onDone: {
                target: '#(machine).$loadedAuthState',
                actions: 'doSetAuthState',
              },
              onError: { target: 'error', actions: 'doLogStorageError' },
            },
          },
          error: {
            on: {
              retryLoadingAuthState: {
                target: 'retrying',
              },
            },
          },
        },
      },

      $loadedAuthState: {
        always: [
          {
            guard: 'isWrongConfiguration',
            target: 'wrongConfiguration',
          },
          {
            guard: 'isCompletedPoll',
            target: 'completedPoll',
          },
          {
            target: 'restoringBeforeAuthState',
          },
        ],
      },

      restoringFromPollsSiteStorage: {
        description: 'Восстановление состояния из хранилища',
        invoke: {
          id: 'persistentStateRestorer',
          src: 'persistentStateRestorer',
          input,
          onDone: { target: '$restoredState', actions: 'doUseFetchedState' },
          onError: { target: '$restoredState', actions: 'doLogStorageError' },
        },
      },

      initServerPassPoll: {
        description: 'Получение структуры опроса с polls site',
        invoke: {
          id: 'initServerPassPoll',
          src: 'initServerPassPoll',
          input,
          onDone: {
            target: '$initializedPoll',
            actions: ['doInitializeServerPassPoll', 'doClearinitServerPassPollErrors'],
          },
          onError: {
            target: 'initServerPassPollError',
            actions: ['doNotifyNetworkError', 'doinitServerPassPollErrorAndIncrementRetryCount'],
          },
        },
      },

      initServerPassPollError: {
        on: {
          retryInitServerPassPoll: {
            description: 'Повтор получения структуры опроса с polls site',
            target: 'initServerPassPoll',
          },
        },
      },

      restoringBeforeAuthState: {
        description: 'Восстановление после редиректа с сервиса авторизации',
        invoke: {
          id: 'beforeAuthStateRestorer',
          src: 'beforeAuthStateRestorer',
          input,
          onDone: { target: '$restoredBeforeAuth', actions: 'doUseFetchedState' },
          onError: { target: '$restoredBeforeAuth', actions: 'doLogStorageError' },
        },
      },

      wrongConfiguration: {
        description: 'Если опрос с шагом авторизации или загрузкой файлов открыт не на polls site',
        type: 'final',
        entry: 'doNotifyWrongConfiguration',
      },

      completedPoll: {
        description: 'Состояние завершённого опроса (из админки или по лимиту времени)',
        entry: 'doNotifyCompleted',
      },

      passedPoll: {
        description: 'Состояние пройденного пользователем опроса',
        entry: 'doNotifyPassed',
      },

      $initializedPoll: {
        always: [
          {
            target: 'transforming',
            guard: 'hasLoadedExternalLogicRules',
          },
          { target: 'loadingExternalLogicRules' },
        ],
      },

      loadingExternalLogicRules: {
        invoke: {
          src: 'externalLogicRulesLoader',
          id: 'externalLogicRulesLoader',
          input,
          onDone: { target: 'transforming', actions: 'doSetExternalLogicRules' },
          onError: { target: 'transforming', actions: 'doLogExternalLogicRulesError' },
        },
      },

      transforming: {
        always: [
          {
            target: '$transformed',
            guard: 'isNotServerPass',
            actions: 'doPreStartTransformPoll',
          },
          { target: '$transformed' },
        ],
      },

      $transformed: {
        always: [
          {
            target: 'savingStateToStorage',
            guard: 'isShouldRestoreState',
          },
          { target: '$loaded' },
        ],
      },

      savingStateToStorage: {
        description:
          'Сохранение состояния прохождения опроса для обеспечения консистентности внешних логических условия, рандомизации и т.д.',
        invoke: {
          src: 'immediatelyUpdateStorageState',
          id: 'immediatelyUpdateStorageState',
          input,
          onDone: { target: '$loaded' },
          onError: { target: '$loaded' },
        },
      },

      $loaded: {
        entry: 'doTrackLoad',
        always: [
          {
            guard: 'isPassedPoll',
            target: 'passedPoll',
          },
          {
            target: '$ready',
          },
        ],
      },

      $ready: {
        entry: 'doNotifyReady',
        always: [
          {
            guard: 'isReinit',
            target: 'questions',
            actions: 'doSetTrackingUserIfHadAnsweredAuthSuccessfully',
          },
          {
            guard: 'isWithWelcome',
            target: 'welcome',
            actions: 'doSetWelcome',
          },
          {
            guard: 'isWithGlobalTimer',
            target: 'globalTimerWelcome',
            actions: 'doInit',
          },
          {
            target: 'questions',
            actions: 'doInit',
          },
        ],
      },

      welcome: {
        on: {
          welcomeClick: {
            target: 'questions',
            actions: ['doWelcomeClick', 'doTrackWelcomeClick'],
          },
        },
      },

      globalTimerWelcome: {
        on: {
          globalTimerStart: {
            target: 'questions',
            actions: 'doStartGlobalTimer',
          },
        },
      },

      questions: {
        initial: 'screen',

        states: {
          $switching: {
            entry: 'doTrackImageVisibility',
            always: [{ target: 'screen', actions: 'doUseNextState' }],
          },

          $switchingBack: {
            always: {
              target: 'screen.changed.submittable.unlocked',
              actions: 'doBack',
            },
          },

          screen: {
            initial: 'tracked',

            on: {
              questionFocus: {
                actions: 'doTrackStart',
              },

              mediaProgressChange: {
                actions: ['doTrackStart', 'doMediaProgressChange'],
              },

              mediaProgress: {
                actions: 'doTrackMediaProgress',
              },
            },

            states: {
              changed: {
                initial: '$validating',
                on: {
                  back: { target: '#(machine).questions.$switchingBack', guard: 'isCanGoBack' },
                  skip: { target: '$skipping', guard: 'isAllQuestionsSkippableAndAutoCommit' },
                },
                always: {
                  guard: 'isAllQuestionsUnchanged',
                  target: 'tracked',
                },
                states: {
                  $validating: {
                    always: [{ guard: 'isInvalidAnswerChange', target: 'invalid' }, 'submittable'],
                  },

                  submittable: {
                    on: {
                      answer: '$submitted',
                    },

                    initial: 'unlocked',

                    states: {
                      locked: {},

                      unlocked: {
                        after: {
                          REMAINED_TIMER: [
                            {
                              guard: 'isStrictTimer',
                              target: 'locked',
                              actions: 'doMarkTimeExpired',
                            },
                            {
                              actions: 'doMarkTimeExpired',
                            },
                          ],
                        },

                        on: {
                          change: { target: '#(machine).questions.screen.changed.$validating', actions: 'doChange' },

                          othersChange: {
                            target: '#(machine).questions.screen.changed.$validating',
                            actions: 'doOthersChange',
                          },

                          fileUpload: {
                            target: '#(machine).questions.screen.changed.$validating',
                            actions: ['doTrackFileUpload', 'doFileUpload'],
                          },

                          skip: {
                            target: '#(machine).questions.screen.$skipping',
                            guard: 'isAllQuestionsSkippableAndPoll',
                          },
                        },

                        always: {
                          guard: 'isAutoCommitable',
                          actions: 'doMarkAutoCommited',
                          target: '#(machine).questions.screen.changed.$submitted',
                        },
                      },
                    },
                  },

                  $submitted: {
                    always: [
                      {
                        guard: 'isInvalidAnswerOrUnanswerable',
                        actions: ['doMarkInvalidAnswers', 'doMarkInvalidUnanswerable'],
                        target: 'invalid',
                      },

                      {
                        actions: 'doMarkAnswered',
                        target: '#(machine).questions.screen.tracking',
                      },
                    ],
                  },

                  invalid: {
                    initial: 'unlocked',

                    states: {
                      unlocked: {
                        on: {
                          change: { target: '#(machine).questions.screen.changed.$validating', actions: 'doChange' },

                          othersChange: {
                            target: '#(machine).questions.screen.changed.$validating',
                            actions: 'doOthersChange',
                          },

                          fileUpload: {
                            target: '#(machine).questions.screen.changed.$validating',
                            actions: ['doTrackFileUpload', 'doFileUpload'],
                          },

                          tryAnswer: '#(machine).questions.screen.changed.$submitted',
                        },
                        after: {
                          REMAINED_TIMER: [
                            {
                              guard: 'isStrictTimer',
                              target: 'locked',
                              actions: 'doMarkTimeExpired',
                            },
                            {
                              actions: 'doMarkTimeExpired',
                            },
                          ],
                        },
                      },

                      locked: {
                        on: {
                          skip: { target: '#(machine).questions.screen.$skipping', actions: 'doTrackStart' },
                        },
                      },
                    },
                  },
                },
              },

              tracked: {
                on: {
                  change: { target: 'changed', actions: ['doTrackStart', 'doChange'] },
                  othersChange: { target: 'changed', actions: ['doTrackStart', 'doOthersChange'] },
                  fileUpload: { target: 'changed', actions: ['doTrackFileUpload', 'doFileUpload'] },

                  back: {
                    target: '#(machine).questions.$switchingBack',
                    guard: 'isCanGoBack',
                  },

                  tryAnswer: { target: 'changed.$submitted', actions: ['doTrackStart'] },

                  skip: { target: '$skipping', actions: 'doTrackStart', guard: 'isAllQuestionsSkippable' },
                },
                after: {
                  REMAINED_TIMER: [
                    {
                      guard: 'isStrictTimer',
                      target: 'expired',
                      actions: 'doMarkTimeExpired',
                    },
                    {
                      actions: 'doMarkTimeExpired',
                    },
                  ],
                },
                always: [
                  { guard: 'isOnAuthStep', target: 'auth' },
                  {
                    guard: 'isSomeQuestionsChanged',
                    target: 'changed',
                  },
                  {
                    guard: 'isAllQuestionsUnanswerable',
                    target: 'unanswerable',
                  },
                ],
              },

              trackingError: {
                on: {
                  retryPixelSend: {
                    description: 'Событие повтора отправки пикселей',
                    target: '#(machine).questions.screen.tracking.sendingPixels',
                  },
                },
              },

              tracking: {
                initial: 'calculatingNext',
                states: {
                  calculatingNext: {
                    invoke: {
                      id: 'nextCalculator',
                      src: 'nextCalculator',
                      input,
                      onDone: {
                        target: 'sendingPixels',
                        actions: ['doSetNextState', 'doClearNextCalculatorErrors'],
                      },
                      onError: [
                        {
                          guard: 'isUnauthorizedErrorWithAuthStep',
                          target: 'nextCalculatorUnauthorizedError',
                          actions: ['doNotifyNetworkError'],
                        },
                        {
                          guard: 'isForbiddenErrorWithAuthStep',
                          target: '#(machine).forbiddenError',
                          actions: ['doNotifyNetworkError'],
                        },
                        {
                          target: 'nextCalculatorError',
                          actions: ['doNotifyNetworkError', 'doAddNextCalculatorErrorAndIncrementRetryCount'],
                        },
                      ],
                    },
                  },
                  sendingPixels: {
                    invoke: {
                      id: 'pixelSender',
                      src: 'pixelSender',
                      input,
                      onDone: {
                        target: '#(machine).questions.screen.answered',
                        actions: ['doClearTrackingErrors', 'doSetLastPixelHash', 'doMarkAnswerTrackingStatus'],
                      },
                      onError: {
                        target: '#(machine).questions.screen.trackingError',
                        actions: ['doNotifyNetworkError', 'doAddTrackingErrorAndIncrementRetryCount'],
                      },
                    },
                  },
                  nextCalculatorError: {
                    on: {
                      retryNextCalculator: {
                        description: 'Событие повтора отправки next action',
                        target: '#(machine).questions.screen.tracking.calculatingNext',
                      },
                    },
                  },
                  nextCalculatorUnauthorizedError: {
                    on: {
                      refreshAuthentication: {
                        target: '#(machine).questions.screen.auth.dumpingStateBeforeAuth',
                        actions: 'doClearTokenInStorage',
                      },
                    },
                  },
                },
                entry: ['doTrackImageVisibility'],
              },

              answered: {
                initial: '$checkingFeedback',
                states: {
                  $checkingFeedback: {
                    always: [
                      {
                        guard: 'isWithImageFeedback',
                        target: '#(machine).questions.$switching',
                      },
                      {
                        guard: 'hasFeedback',
                        target: 'feedback',
                      },
                      {
                        target: '#(machine).questions.$switching',
                      },
                    ],
                  },
                  feedback: {
                    on: {
                      next: { target: '#(machine).questions.$switching' },
                      back: { target: '#(machine).questions.$switchingBack', guard: 'isCanGoBack' },
                    },
                  },
                },
                exit: 'doTrackImageVisibility',
              },

              auth: {
                initial: '$init',
                states: {
                  $init: {
                    always: [
                      {
                        guard: 'isReturningFromAuth',
                        target: '$returning',
                      },
                      {
                        guard: 'isAuthenticatedWithSkipAuthenticated',
                        target: '$continuing',
                      },
                      'unanswered',
                    ],
                  },

                  unanswered: {
                    on: {
                      continueAuthenticated: {
                        guard: 'isAuthenticated',
                        actions: ['doTrackStart'],
                        target: '$continuing',
                      },
                      startAuthentication: {
                        guard: 'isNotAuthenticated',
                        actions: ['doTrackStart', 'doChangeToAuthorizedAnswer'],
                        target: 'dumpingStateBeforeAuth',
                      },
                      skipAuthentication: {
                        guard: 'isNotRequiredAuth',
                        actions: ['doTrackStart', 'doChangeToUnauthorizedAnswer'], // TODO: move doTrackStart to exit effect of tracked and make tracked state compound
                        target: '$answering',
                      },
                    },
                  },

                  retrying: {
                    on: {
                      startAuthentication: {
                        actions: ['doChangeToAuthorizedAnswer'],
                        target: 'dumpingStateBeforeAuth',
                      },
                      skipAuthentication: {
                        guard: 'isNotRequiredAuth',
                        actions: ['doChangeToUnauthorizedAnswer'],
                        target: '$answering',
                      },
                    },
                  },

                  dumpingStateBeforeAuth: {
                    invoke: {
                      src: 'beforeAuthStateDumper',
                      id: 'beforeAuthStateDumper',
                      input,
                      onDone: {
                        actions: 'doRedirectToLogin',
                        target: 'redirecting',
                      },
                      onError: {
                        target: 'redirecting',
                        actions: ['doLogStorageError'],
                      },
                    },
                  },

                  redirecting: {
                    type: 'final',
                  },

                  $continuing: {
                    always: {
                      actions: ['doSetTrackingUser', 'doChangeToAuthorizedAnswer'],
                      target: '$answering',
                    },
                  },

                  $answering: {
                    always: {
                      actions: 'doMarkAnswered',
                      target: '#(machine).questions.screen.tracking',
                    },
                  },

                  $returning: {
                    always: [
                      {
                        guard: 'isAuthenticated',
                        actions: ['doSetTrackingUser'],
                        target: '$answering',
                      },
                      {
                        actions: ['doChangeToUnauthorizedAnswer', 'doLogAuthErrorAfterRedirect'],
                        target: 'retrying',
                      },
                    ],
                  },
                },
              },

              unanswerable: {
                initial: 'init',
                states: {
                  init: {
                    on: {
                      next: { target: '$validating', actions: 'doTrackStart' },
                      mediaProgressChange: {
                        target: 'init',
                        actions: ['doTrackStart', 'doMediaProgressChange'],
                      },
                      back: {
                        target: '#(machine).questions.$switchingBack',
                        guard: 'isCanGoBack',
                      },
                    },
                  },

                  $validating: {
                    entry: 'doMarkInvalidUnanswerable',
                    always: [
                      { target: '#(machine).questions.screen.tracking', guard: 'isValidUnanswerable' },
                      { target: 'init' },
                    ],
                  },
                },
              },

              expired: {
                on: {
                  skip: { target: '$skipping', actions: 'doTrackStart' },
                },
              },

              $skipping: {
                entry: 'doMarkSkipped',
                always: {
                  target: 'tracking',
                },
              },
            },

            always: [
              {
                guard: 'isReadOnlyLastScreen',
                target: '#(machine).final',
              },
              {
                guard: 'isFinalScreen',
                target: '#(machine).final',
              },
            ],
          },
        },
      },

      final: {
        initial: '$init',

        states: {
          $init: {
            entry: ['doSetFinishedAt', 'doNotifyDone', 'doNotifyDoneRequests', 'doNotifyOverall'],

            always: [
              {
                guard: 'isInstantAutoClose',
                actions: 'doAutoClose',
                target: '#(machine).instantlyAutoClosed',
              },
              {
                guard: 'hasAutoClose',
                target: 'closingFinished',
              },
              {
                target: 'finished',
              },
            ],
          },

          finished: {},

          closingFinished: {
            after: {
              AUTO_CLOSE_TIMEOUT: {
                target: 'finished',
                actions: 'doAutoClose',
              },
            },
          },
        },

        on: {
          finalClick: {
            actions: 'doTrackFinalClick',
            target: 'final',
          },
        },
      },

      $closing: {
        entry: 'doClose',
        always: 'closed',
      },

      closed: {},

      instantlyAutoClosed: {},

      forbiddenError: {},

      $restoredBeforeAuth: {
        always: [
          {
            guard: 'isShouldRestoreState',
            target: 'restoringFromPollsSiteStorage',
          },
          { target: '$restoredState' },
        ],
      },

      $restoredState: {
        always: [
          {
            guard: 'isServerPass',
            target: 'initServerPassPoll',
          },
          { target: '$initializedPoll' },
        ],
      },
    },
  });
}
/** Входные данные для внешних сервисов (акторов) */
const input = ({ context }: { context: PollMachineContext }) => context;

function isAutoCommitable({ context: { state, poll } }: { context: { state: ICState; poll: Poll } }) {
  const answersMap = getMapFromStack(state.answersStack);
  const group = state.groupId ? selectGroup(poll, state.groupId) : null;

  return selectRelevantQuestions({ state, poll }).every(q => {
    const autoCommit = group?.data.autoCommit ?? ('autoCommit' in q.data && q.data.autoCommit);
    const { lastTouchedBy, isWithoutAnswers } = findLastInStack(state.answersStack, q.id);
    const isTouchedByUser = lastTouchedBy === 'user';
    const hasTextfield = questionHasDisclaimer(q, answersMap[q.id]);
    const isMultiple = getEngine(poll.template).hasTrait(q.data.type, 'MULTIPLE_ANSWERS_TYPE');

    return (autoCommit && isTouchedByUser && !hasTextfield && !isMultiple) || isWithoutAnswers;
  });
}

function isAllQuestionsSkippable({ context: { state, poll } }: { context: { state: ICState; poll: Poll } }) {
  return selectRelevantQuestions({ state, poll })
    .filter(q => !findLastInStack(state.answersStack, q.id).isWithoutAnswers)
    .every(
      q =>
        ('requiresAnswer' in q.data && !q.data.requiresAnswer) ||
        (q.data.type === 'POLL_PROTOTYPE_SCREEN' &&
          (q.data.answers.filter(a => 'area' in a).length === 0 || q.data.isFinal)),
    );
}

const isInvalidUnanswerable = ({ state, poll, intl }: { state: ICState; poll: Poll; intl: InterpreterIntl }) => {
  const unanswerableQuestions = selectRelevantQuestions({ state, poll })
    .map(question => ({
      question,
      answerState: findLastInStack(state.answersStack, question.id),
    }))
    .filter(({ answerState }) => findLastInStack(state.answersStack, answerState.id).isWithoutAnswers)
    .map(({ question }) => ({
      question,
      answerState: findLastInStack(state.answersStack, question.id),
    }));

  return unanswerableQuestions.some(({ question, answerState }) => !!validateUnanswerable(question, answerState, intl));
};

const isInvalidAnswer = ({ state, poll, intl }: { state: ICState; poll: Poll; intl: InterpreterIntl }) =>
  selectRelevantQuestions({ state, poll }).some(
    (question, _, relevantQuestions) => !!validateAnswers(question, relevantQuestions, state.answersStack, intl),
  );

function getTimerCreatedAt(state: ICState, poll: Poll) {
  if (state.groupId) {
    const questions = selectDisplayedOrAllGroupQuestions(poll, state.groupId, state.answersStack);

    for (const question of questions) {
      const found = findLastInStackSafe(state.answersStack, question.id);
      if (found && found.createdAt) return found.createdAt;
    }
  } else if (state.questionId) {
    const found = findLastInStackSafe(state.answersStack, state.questionId);
    if (found && found.createdAt) return found.createdAt;
  }

  return undefined;
}
function getScreenTimerInfo({ poll, state: { groupId, questionId } }: { poll: Poll; state: ICState }) {
  if (groupId) {
    const group = selectGroup(poll, groupId);
    if (group.data.hasTimer) {
      return {
        timerDuration: group.data.timerDuration ?? 0,
        timerStrict: Boolean(group.data.timerStrict),
        timerExpiredText: group.data.timerExpiredText,
      };
    }
    /** Таймер на вопрос */
  } else if (questionId) {
    const question: {
      data: Wide<{ hasTimer?: boolean; timerExpiredText?: string; timerStrict?: boolean; timerDuration?: number }>;
    } = selectQuestion(poll, questionId);

    if (question.data.hasTimer) {
      return {
        timerDuration: question.data.timerDuration ?? 0,
        timerStrict: Boolean(question.data.timerStrict),
        timerExpiredText: question.data.timerExpiredText,
      };
    }
  }
  return undefined;
}

/** Объявляет тип, который принимает объекты без указанных свойств (но и без конфликтов)
 * ```
 * const ex0:      { a: number }  = { b: "str" }; // error!
 * const ex1: Wide<{ a: number }> = { b: "str" }; // ok
 * const ex2: Wide<{ a: number }> = { a: 123, b: "str" }; // ok
 * const ex3: Wide<{ a: number }> = { a: "str" }; // error!
 * ```
 */
declare type Wide<T> = T & object;

function canClosePoll({
  state,
  poll,
  waitOnlySystemPixels,
}: {
  state: ICState;
  poll: Poll;
  waitOnlySystemPixels?: boolean;
}) {
  return !shouldWaitForPixels({ state, poll }) || isAllPixelSent({ state, onlySystem: waitOnlySystemPixels });
}

function shouldWaitForPixels({ state, poll }: { state: ICState; poll: Poll }) {
  const hasPixels = !!poll.pixels.length;
  const isFinished = !!state.finishedAt;
  return hasPixels && isFinished;
}

function isAllPixelSent({ state, onlySystem = false }: { state: ICState; onlySystem?: boolean }) {
  return (
    !!state.trackingStack.length &&
    state.trackingStack.every(trackingState =>
      trackingState.tracking.every(track => {
        if (track.status !== 'pending') return true;
        return onlySystem && !track.isSystem;
      }),
    )
  );
}
